import {
	Box,
	Button,
	Divider,
	IconButton,
	InputAdornment,
	InputBase,
	Menu,
	MenuItem,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import queryString from 'query-string';
import { Close } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { COLORS } from 'services/constants/colors';
import { ETableStructureType } from 'services/constants/tableStructureTypes';
import { TSeparateFilterProps } from './types';

export const SeparateFilter = ({ name, selector, type, filter }: TSeparateFilterProps) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);

	const [changed, setChanged] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [debouncedValue] = useDebounce(inputValue, 1000);
	const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);

	const isStatusType = type === ETableStructureType.STATUS;
	const statusOptions = filter ?? [];

	const paramSelector = `f-${selector}`;
	const paramValueStr =
		navigateParams[paramSelector] != null && navigateParams[paramSelector] !== ''
			? String(navigateParams[paramSelector])
			: '';

	const selectedStatusLabel = statusOptions.find((o) => String(o[0]) === paramValueStr)?.[1] ?? '';

	useEffect(() => {
		if (isStatusType) return;
		if (changed) {
			navigate({
				pathname: location.pathname,
				search: `?${queryString.stringify({
					...navigateParams,
					[`f-${selector}`]: debouncedValue || undefined,
					page: 1,
				})}`,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedValue]);

	useEffect(() => {
		if (isStatusType) return;
		const paramValue = (navigateParams[`f-${selector}`] as string) || '';
		setInputValue(paramValue);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search]);

	const navigateWithParam = (value: string | undefined) => {
		navigate({
			pathname: location.pathname,
			search: `?${queryString.stringify({
				...navigateParams,
				[`f-${selector}`]: value,
				page: 1,
			})}`,
		});
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setChanged(true);
		setInputValue(e.target?.value ?? '');
	};

	const handleClear = () => {
		setChanged(true);
		setInputValue('');
		if (isStatusType) {
			navigateWithParam(undefined);
		}
	};

	const handleStatusSelect = (optionValue: string | number | null) => {
		const serialized =
			optionValue === null ? 'null' : optionValue === '' ? undefined : String(optionValue);
		navigateWithParam(serialized);
		setStatusMenuAnchor(null);
	};

	return (
		<Box
			component="div"
			sx={{
				display: 'flex',
				alignItems: 'center',
				borderRadius: '12px',
				borderWidth: '1px',
				borderStyle: 'solid',
				borderColor: 'primary.main',
				fontSize: '.9rem',
				p: 0,
				m: 0,
			}}
		>
			{!isStatusType && (
				<>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
						<Typography
							variant="body1"
							color="text.secondary"
							fontWeight={500}
							sx={{
								p: '2px 4px',
								fontSize: '.9rem',
								color: COLORS.PRIMARY,
							}}
						>
							{name}:
						</Typography>
					</Box>
					<InputBase
						sx={{
							flex: 1,
							fontSize: '.9rem',
							p: '2px 0px',
							'& input': {
								padding: '4px 0px',
								fontSize: '.9rem',
								'&::placeholder': {
									fontSize: '.8rem',
								},
							},
						}}
						style={{ padding: 0 }}
						size="small"
						placeholder={`Enter ${name}`}
						value={inputValue}
						onChange={handleChange}
						// endAdornment={
						// 	inputValue ? (
						// 		<InputAdornment position="end">
						// 			<IconButton size="small" onClick={handleClear} sx={{ p: 0 }}>
						// 				<Close fontSize="small" />
						// 			</IconButton>
						// 		</InputAdornment>
						// 	) : null
						// }
					/>
					<Divider sx={{ height: 18, m: 0.5 }} orientation="vertical" />
					<IconButton
						color="primary"
						sx={{ p: '0 5px 0 0' }}
						aria-label="Clear filter"
						onClick={handleClear}
					>
						<Close fontSize="small" />
					</IconButton>
				</>
			)}
			{isStatusType && (
				<>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
						<Typography
							variant="body1"
							color="text.secondary"
							fontWeight={500}
							sx={{
								p: '1px 4px',
								fontSize: '.9rem',
								color: COLORS.PRIMARY,
							}}
						>
							{name}:
						</Typography>
					</Box>
					<Button
						id="separate-status-filter"
						variant="text"
						size="small"
						onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
						endIcon={<ExpandMoreIcon fontSize="small" />}
						sx={{
							flex: 1,
							textTransform: 'none',
							color: 'primary.main',
							fontWeight: 'bold',
							fontSize: '.9rem',
							justifyContent: 'space-between',
							p: 0,
							m: 0,
							minWidth: 0,
						}}
					>
						{selectedStatusLabel || `Select ${name}`}
					</Button>
					<Menu
						anchorEl={statusMenuAnchor}
						open={Boolean(statusMenuAnchor)}
						onClose={() => setStatusMenuAnchor(null)}
						MenuListProps={{ 'aria-labelledby': 'separate-status-filter' }}
						sx={{
							'& .MuiMenu-paper': { borderRadius: '12px' },
						}}
					>
						{statusOptions.map((optionItem) => (
							<MenuItem
								key={String(optionItem[0])}
								onClick={() => handleStatusSelect(optionItem[0])}
								selected={String(optionItem[0]) === paramValueStr}
								sx={{ color: 'primary.main', fontWeight: 'bold' }}
							>
								{optionItem[1]}
							</MenuItem>
						))}
					</Menu>
					<Divider sx={{ height: 18, m: 0.5 }} orientation="vertical" />
					<IconButton
						color="primary"
						sx={{ p: '0 5px 0 0', m: 0 }}
						aria-label="Clear filter"
						onClick={handleClear}
						disabled={!paramValueStr}
					>
						<Close fontSize="small" sx={{ fontSize: '14px' }} />
					</IconButton>
				</>
			)}
		</Box>
	);
};
